/**
 * Notification rendering utilities
 */
import { translator } from "../translation/translator";
import { timestampHelper } from "../core/timestamp/timestamp";
import { notificationsForm } from "./notifications-form";
import { bindNotificationItemActivation } from "./notifications-listener";
import { CommonUtils } from "../util/common-utils";
import { NotificationItem } from "./notifications-api";

type UnknownRecord = Record<string, unknown>;

export interface NotificationListClickPayload {
    event?: Event;
    notificationId: number;
    linkUrl: string | null;
    itemEl: HTMLElement;
}

interface RenderNotificationListOptions {
    onItemClick?: (payload: NotificationListClickPayload) => Promise<void> | void;
}

function normalizeDataJson(raw: unknown): UnknownRecord {
    return raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as UnknownRecord) : {};
}

function getLangSuffix(): "th" | "en" | "ja" {
    const lang = translator.getCurrentLanguage();
    if (lang === "th" || lang === "en" || lang === "ja") return lang;
    return "en";
}

function resolveTitle(notification: NotificationItem): string {
    const lang = getLangSuffix();
    const item = notification as UnknownRecord;
    const title = String(item[`notification_title_${lang}`] ?? "").trim();
    return title;
}

function resolveBody(notification: NotificationItem): string {
    const lang = getLangSuffix();
    const item = notification as UnknownRecord;
    return String(item[`notification_body_${lang}`] ?? "").trim();
}

function resolveAvatarUrl(dataJson: UnknownRecord): string {
    const candidates = [
        dataJson?.avatar_url,
        dataJson?.follower_avatar_url,
        dataJson?.actor_avatar_url,
    ];
    for (const candidate of candidates) {
        const raw = String(candidate ?? "").trim();
        if (raw) return CommonUtils.resolveUrl(raw);
    }
    return "";
}

function buildAvatar(sendType: string, dataJson: UnknownRecord, actorUserId: unknown): string {
    const avatarUrl = resolveAvatarUrl(dataJson);

    if (sendType === "followed") {
        const followerId = Number(actorUserId ?? dataJson?.follower_id ?? 0);
        const href = CommonUtils.escapeHtmlAttribute(
            followerId > 0 ? CommonUtils.resolveUrl(`/users/${followerId}`) : "#",
        );
        const displayName = String(dataJson?.follower_display_name ?? "").trim();
        const label = CommonUtils.escapeHtml(displayName ? displayName.charAt(0).toUpperCase() : "U");

        if (avatarUrl) {
            const safeUrl = CommonUtils.escapeHtmlAttribute(avatarUrl);
            return `<a class="navbar-notification-avatar-link" href="${href}" aria-label="Open follower profile">
                        <img class="navbar-notification-avatar" src="${safeUrl}" alt="" loading="lazy">
                    </a>`;
        }
        return `<a class="navbar-notification-avatar-link" href="${href}" aria-label="Open follower profile">
                    <span class="navbar-notification-avatar navbar-notification-avatar--fallback">${label}</span>
                </a>`;
    }

    if (sendType === "mentioned") {
        const actorId = Number(dataJson?.actor_id ?? actorUserId ?? 0);
        const href = CommonUtils.escapeHtmlAttribute(
            actorId > 0 ? CommonUtils.resolveUrl(`/users/${actorId}`) : "#",
        );
        const displayName = String(dataJson?.actor_display_name ?? "").trim();
        const label = CommonUtils.escapeHtml(displayName ? displayName.charAt(0).toUpperCase() : "U");

        if (avatarUrl) {
            const safeUrl = CommonUtils.escapeHtmlAttribute(avatarUrl);
            return `<a class="navbar-notification-avatar-link" href="${href}" aria-label="Open actor profile">
                        <img class="navbar-notification-avatar" src="${safeUrl}" alt="" loading="lazy">
                    </a>`;
        }
        return `<a class="navbar-notification-avatar-link" href="${href}" aria-label="Open actor profile">
                    <span class="navbar-notification-avatar navbar-notification-avatar--fallback">${label}</span>
                </a>`;
    }

    if (avatarUrl) {
        const safeUrl = CommonUtils.escapeHtmlAttribute(avatarUrl);
        return `<img class="navbar-notification-avatar" src="${safeUrl}" alt="" loading="lazy">`;
    }

    return `<span class="navbar-notification-avatar navbar-notification-avatar--system" aria-hidden="true">
                <i class="bi bi-bell-fill"></i>
            </span>`;
}

export function updateBadge(badgeEl: Element | null, count: number): void {
    if (!(badgeEl instanceof HTMLElement)) return;
    const safe = Number.isFinite(count) && count > 0 ? count : 0;
    badgeEl.textContent = String(safe);
    badgeEl.hidden = safe === 0;
}

export function setLoading(loadingEl: HTMLElement, isLoading: boolean): void {
    if (!(loadingEl instanceof HTMLElement)) return;
    loadingEl.hidden = !isLoading;
    loadingEl.style.display = isLoading ? "flex" : "none";
}

interface ShowEmptyOptions {
    visible?: boolean;
    translateKey?: string | null;
}

export function showEmpty(emptyEl: HTMLElement, { visible, translateKey = null }: ShowEmptyOptions = {}): void {
    if (!(emptyEl instanceof HTMLElement)) return;
    if (translateKey) {
        emptyEl.setAttribute("data-translate", translateKey);
    }
    emptyEl.hidden = !visible;
    translator.updateContent();
}

export function renderNotificationList(
    listEl: HTMLElement,
    items: NotificationItem[],
    { onItemClick }: RenderNotificationListOptions,
): void {
    if (!(listEl instanceof HTMLElement)) return;

    const htmlParts: string[] = [];

    for (const notification of items) {
        const notificationId = Number(notification?.notification_id ?? 0);
        const sendType = String(notification?.send_type ?? "");
        const linkUrl = notification?.link_url ? String(notification.link_url) : null;
        const createdAtRaw = notification?.created_at ? String(notification.created_at) : "";
        const createdAtAttr = createdAtRaw ? CommonUtils.escapeHtmlAttribute(createdAtRaw) : "";
        const dataJson = normalizeDataJson(notification?.data_json);
        const actorUserId = notification?.actor_user_id ?? dataJson?.actor_user_id ?? dataJson?.follower_id;

        const titleText = CommonUtils.escapeHtml(resolveTitle(notification));
        // Body may contain server-rendered HTML (<a>, <strong>) — trusted content from own DB
        const bodyHtml = resolveBody(notification);
        const avatarHtml = buildAvatar(sendType, dataJson, actorUserId);

        const unreadClass = notification?.read_at ? "" : " is-unread";
        const safeHref = linkUrl && !/^javascript:/i.test(linkUrl) ? CommonUtils.resolveUrl(linkUrl) : "#";
        const href = CommonUtils.escapeHtmlAttribute(safeHref);

        const metaHtml = createdAtRaw
            ? `<div class="navbar-notification-meta"><span class="timestamp-relative" data-timestamp="${createdAtAttr}"></span></div>`
            : "";

        htmlParts.push(`
            <li>
                <div class="navbar-notification-item${unreadClass}"
                   role="link"
                   tabindex="0"
                   data-notification-id="${notificationId}"
                   data-link-url="${href}">
                    <div class="navbar-notification-avatar-wrap">
                        ${avatarHtml}
                    </div>
                    <div class="navbar-notification-content">
                        <div class="navbar-notification-title">
                            ${titleText}
                        </div>
                        <div class="navbar-notification-body">
                            ${bodyHtml}
                        </div>
                        ${metaHtml}
                    </div>
                </div>
            </li>
        `);
    }

    listEl.innerHTML = htmlParts.join("");

    const itemsEls = notificationsForm.getNotificationItems(listEl);
    itemsEls.forEach((itemEl) => {
        const notificationId = Number(itemEl.dataset.notificationId ?? 0);
        const linkUrl = itemEl.getAttribute("data-link-url");

        const activate = (event: Event): void => {
            const target = event?.target;
            const innerA = notificationsForm.getClosestAnchor(target);

            void onItemClick?.({
                event,
                notificationId,
                linkUrl,
                itemEl,
            });

            if (innerA) return;

            if (linkUrl && linkUrl !== "#") {
                window.location.href = CommonUtils.resolveUrl(String(linkUrl));
            }
        };

        bindNotificationItemActivation(itemEl, activate, (event: Event) => {
            if (!(event instanceof KeyboardEvent)) return;
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                activate(event);
            }
        });
    });

    timestampHelper.updateAllTimestamps();
}

export function clearNotificationList(listEl: HTMLElement): void {
    CommonUtils.setHtml(listEl, "");
}
